/**
 * Local order book maintained from Binance diff-depth updates.
 *
 * This implements Binance's published spot synchronisation procedure exactly,
 * because the shortcut — subscribing to `@depth20` and painting whatever
 * arrives — gives you a book that is only ever 20 levels deep, cannot support
 * a depth histogram, and silently shows stale levels whenever an update is
 * dropped. The real procedure is:
 *
 *   1. Start buffering diff events before requesting anything.
 *   2. Fetch a REST snapshot with its own `lastUpdateId`.
 *   3. If the snapshot predates the first buffered event, it is useless —
 *      fetch again.
 *   4. Discard buffered events entirely covered by the snapshot.
 *   5. Seed from the snapshot, then apply the remaining buffer in order.
 *   6. From then on, every event must start exactly where the last one
 *      finished; a gap means we have lost updates and the book is wrong.
 *
 * Step 6 is the one worth being strict about. A book that has silently missed
 * an update looks completely normal and quotes a spread that does not exist.
 * Detecting the gap and resynchronising is the only safe response.
 */

import type { RestDepth, WsDepth } from './binance';

export type BookState = 'empty' | 'syncing' | 'live' | 'resyncing';

/** Flattened, sorted view handed to renderers. Reused across frames. */
export interface BookLevels {
  bidPrice: Float64Array;
  bidQty: Float64Array;
  bidCum: Float64Array;
  bidCount: number;

  askPrice: Float64Array;
  askQty: Float64Array;
  askCum: Float64Array;
  askCount: number;

  /** Largest single resting size on either side, for row bar scaling. */
  maxQty: number;
  /** Largest cumulative total on either side, for the depth histogram. */
  maxCum: number;

  bestBid: number;
  bestAsk: number;
  spread: number;
  mid: number;
}

const FLASH_MS = 420;

function emptyLevels(cap: number): BookLevels {
  return {
    bidPrice: new Float64Array(cap),
    bidQty: new Float64Array(cap),
    bidCum: new Float64Array(cap),
    bidCount: 0,
    askPrice: new Float64Array(cap),
    askQty: new Float64Array(cap),
    askCum: new Float64Array(cap),
    askCount: 0,
    maxQty: 0,
    maxCum: 0,
    bestBid: NaN,
    bestAsk: NaN,
    spread: NaN,
    mid: NaN,
  };
}

export class OrderBook {
  private bids = new Map<number, number>();
  private asks = new Map<number, number>();

  private lastUpdateId = 0;
  private buffer: WsDepth[] = [];
  private snapshotPending = false;

  state: BookState = 'empty';
  /** Bumped on every applied mutation. */
  seq = 0;
  /** How many times we have had to rebuild from a fresh snapshot. */
  resyncCount = 0;
  lastResyncReason: string | null = null;

  /** price -> timestamp of last size change, for the flash highlight. */
  private changed = new Map<number, number>();

  private levels = emptyLevels(2048);
  private levelsSeq = -1;
  private levelsGroup = 0;
  private levelsDepth = 0;

  constructor(readonly symbol: string) {}

  // -- synchronisation ------------------------------------------------------

  /** Called before the snapshot request goes out. */
  beginSync(): void {
    this.state = this.state === 'live' ? 'resyncing' : 'syncing';
    this.buffer.length = 0;
    this.snapshotPending = true;
    this.seq++;
  }

  /**
   * Seed from a REST snapshot. Returns false when the snapshot is older than
   * the events we already buffered, meaning the caller must fetch again.
   */
  applySnapshot(snap: RestDepth): boolean {
    if (this.buffer.length > 0 && snap.lastUpdateId < this.buffer[0].U) {
      // Step 3: snapshot predates our buffer, so there is a hole between them.
      return false;
    }

    this.bids.clear();
    this.asks.clear();
    for (const [p, q] of snap.bids) {
      const qty = +q;
      if (qty > 0) this.bids.set(+p, qty);
    }
    for (const [p, q] of snap.asks) {
      const qty = +q;
      if (qty > 0) this.asks.set(+p, qty);
    }
    this.lastUpdateId = snap.lastUpdateId;
    this.snapshotPending = false;

    // Step 4/5: replay the buffer, dropping anything the snapshot covers.
    const pending = this.buffer;
    this.buffer = [];
    for (const ev of pending) {
      if (ev.u <= this.lastUpdateId) continue;
      if (this.applyDiff(ev) === 'gap') {
        this.snapshotPending = true;
        this.buffer = pending;
        return false;
      }
    }

    this.state = 'live';
    this.seq++;
    return true;
  }

  /**
   * Feed a diff event. Returns 'gap' when continuity is broken and the caller
   * must resynchronise from a fresh snapshot.
   */
  ingest(ev: WsDepth): 'ok' | 'buffered' | 'gap' {
    if (this.snapshotPending || this.state === 'empty') {
      this.buffer.push(ev);
      // Do not let a failing snapshot fetch grow this without bound.
      if (this.buffer.length > 500) this.buffer.splice(0, this.buffer.length - 500);
      return 'buffered';
    }
    return this.applyDiff(ev);
  }

  private applyDiff(ev: WsDepth): 'ok' | 'gap' {
    if (ev.u <= this.lastUpdateId) return 'ok'; // already covered

    if (ev.U > this.lastUpdateId + 1) {
      // Step 6 violation: updates between lastUpdateId and ev.U never arrived.
      this.lastResyncReason = `update gap: expected ${this.lastUpdateId + 1}, got ${ev.U}`;
      this.resyncCount++;
      this.state = 'resyncing';
      this.seq++;
      return 'gap';
    }

    const now = performance.now();
    const b = ev.b;
    for (let i = 0; i < b.length; i++) {
      const price = +b[i][0];
      const qty = +b[i][1];
      if (qty === 0) this.bids.delete(price);
      else this.bids.set(price, qty);
      this.changed.set(price, now);
    }
    const a = ev.a;
    for (let i = 0; i < a.length; i++) {
      const price = +a[i][0];
      const qty = +a[i][1];
      if (qty === 0) this.asks.delete(price);
      else this.asks.set(price, qty);
      this.changed.set(price, now);
    }

    this.lastUpdateId = ev.u;
    this.seq++;
    return 'ok';
  }

  /** Drop everything — used when a socket reconnects. */
  invalidate(reason: string): void {
    this.lastResyncReason = reason;
    this.state = this.bids.size ? 'resyncing' : 'empty';
    this.lastUpdateId = 0;
    this.snapshotPending = false;
    this.buffer.length = 0;
    this.seq++;
  }

  // -- read side ------------------------------------------------------------

  get depthCount(): number {
    return this.bids.size + this.asks.size;
  }

  get bestBid(): number {
    let best = -Infinity;
    for (const p of this.bids.keys()) if (p > best) best = p;
    return best === -Infinity ? NaN : best;
  }

  get bestAsk(): number {
    let best = Infinity;
    for (const p of this.asks.keys()) if (p < best) best = p;
    return best === Infinity ? NaN : best;
  }

  /** True if this price level changed recently enough to still be flashing. */
  flashAge(price: number): number {
    const t = this.changed.get(price);
    if (t === undefined) return Infinity;
    return performance.now() - t;
  }

  /**
   * Sorted, cumulated, optionally price-grouped levels.
   *
   * Memoised on (seq, group, depth): the depth stream delivers at 100ms, so
   * at 60fps roughly five of every six calls are served from the cache.
   */
  getLevels(depth: number, group = 0): BookLevels {
    if (this.levelsSeq === this.seq && this.levelsGroup === group && this.levelsDepth >= depth) {
      return this.levels;
    }

    const cap = Math.max(depth, 64);
    if (this.levels.bidPrice.length < cap) this.levels = emptyLevels(cap * 2);
    const L = this.levels;

    L.bidCount = this.collect(this.bids, true, depth, group, L.bidPrice, L.bidQty);
    L.askCount = this.collect(this.asks, false, depth, group, L.askPrice, L.askQty);

    let maxQty = 0;
    let cum = 0;
    for (let i = 0; i < L.bidCount; i++) {
      cum += L.bidQty[i];
      L.bidCum[i] = cum;
      if (L.bidQty[i] > maxQty) maxQty = L.bidQty[i];
    }
    const bidTotal = cum;

    cum = 0;
    for (let i = 0; i < L.askCount; i++) {
      cum += L.askQty[i];
      L.askCum[i] = cum;
      if (L.askQty[i] > maxQty) maxQty = L.askQty[i];
    }

    L.maxQty = maxQty;
    L.maxCum = Math.max(bidTotal, cum);
    L.bestBid = L.bidCount ? L.bidPrice[0] : NaN;
    L.bestAsk = L.askCount ? L.askPrice[0] : NaN;
    L.spread = L.bidCount && L.askCount ? L.askPrice[0] - L.bidPrice[0] : NaN;
    L.mid = L.bidCount && L.askCount ? (L.askPrice[0] + L.bidPrice[0]) / 2 : NaN;

    this.levelsSeq = this.seq;
    this.levelsGroup = group;
    this.levelsDepth = depth;

    this.pruneFlashes();
    return L;
  }

  /**
   * Extract the best `depth` levels from one side, grouped into price buckets.
   *
   * A full sort of every level would be up to 5000 entries; we only ever show
   * a few dozen. Selecting the best `depth * 4` candidates first and sorting
   * those keeps this well inside a frame even on a deep book.
   */
  private collect(
    side: Map<number, number>,
    isBid: boolean,
    depth: number,
    group: number,
    outPrice: Float64Array,
    outQty: Float64Array,
  ): number {
    if (side.size === 0) return 0;

    if (group > 0) {
      // Bucket first, then sort the (much smaller) set of buckets.
      const buckets = new Map<number, number>();
      for (const [price, qty] of side) {
        const b = isBid
          ? Math.floor(price / group) * group
          : Math.ceil(price / group) * group;
        // Round the key: floor(x/g)*g reintroduces float dust that would
        // otherwise split one visual level into two.
        const key = Math.round(b / group) * group;
        buckets.set(key, (buckets.get(key) ?? 0) + qty);
      }
      const keys = [...buckets.keys()].sort(isBid ? (x, y) => y - x : (x, y) => x - y);
      const n = Math.min(depth, keys.length);
      for (let i = 0; i < n; i++) {
        outPrice[i] = keys[i];
        outQty[i] = buckets.get(keys[i])!;
      }
      return n;
    }

    const prices = Array.from(side.keys());
    prices.sort(isBid ? (x, y) => y - x : (x, y) => x - y);
    const n = Math.min(depth, prices.length);
    for (let i = 0; i < n; i++) {
      outPrice[i] = prices[i];
      outQty[i] = side.get(prices[i])!;
    }
    return n;
  }

  /**
   * Total resting size within `pct` of mid, both sides. This is the number
   * that actually says whether a book is thin, and it drives the imbalance
   * readout.
   */
  imbalance(pct = 0.002): { bid: number; ask: number; ratio: number } {
    const mid = (this.bestBid + this.bestAsk) / 2;
    if (!Number.isFinite(mid)) return { bid: 0, ask: 0, ratio: 0.5 };
    const lo = mid * (1 - pct);
    const hi = mid * (1 + pct);
    let bid = 0;
    let ask = 0;
    for (const [p, q] of this.bids) if (p >= lo) bid += q;
    for (const [p, q] of this.asks) if (p <= hi) ask += q;
    const total = bid + ask;
    return { bid, ask, ratio: total > 0 ? bid / total : 0.5 };
  }

  private pruneFlashes(): void {
    if (this.changed.size < 4000) return;
    const now = performance.now();
    for (const [p, t] of this.changed) {
      if (now - t > FLASH_MS) this.changed.delete(p);
    }
  }

  static readonly FLASH_MS = FLASH_MS;
}
